import React, { createContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  InAppCommsPoliciesDocument,
  InAppCommunicationDocument,
  InAppCommunicationQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'

type InAppCommunicationType = NonNullable<
  InAppCommunicationQuery['inAppComms']['communicationsConnection']['nodes']
>[number]

interface DismissedCommInfo {
  id: string
  dismissedAt: number
  name: string
  showAgainAfterDays: number | null
}

interface InAppCommsContextValue {
  activeComms: InAppCommunicationType[]
  dismiss: (componentId: string) => void
}

export const InAppCommsContext = createContext<InAppCommsContextValue | null>(null)

interface InAppCommsProviderProps {
  children: React.ReactNode
  onHasBannerChange?: (hasBanner: boolean) => void
}

export const InAppCommsProvider: React.FC<InAppCommsProviderProps> = ({
  children,
  onHasBannerChange,
}) => {
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()
  const { search, pathname } = useLocation()
  const locale = getDatoLocale(accessLocale)
  const preview = new URLSearchParams(search).get('preview') === 'true'

  const { data } = useQuery(InAppCommunicationDocument, {
    variables: { preview, locale },
    fetchPolicy: 'cache-first',
  })

  const { data: policiesData } = useQuery(InAppCommsPoliciesDocument, {
    fetchPolicy: 'cache-first',
  })

  const canSeeInAppComms = policiesData?.inAppComms?._policies?.canSeeInAppComms?.can ?? false
  const storageKey = LOCAL_STORAGE_KEYS.inAppCommsDismissedIds
  const [dismissedComms, setDismissedComms] = useLocalStorage<DismissedCommInfo[]>(storageKey, [])
  const [selectedComms, setSelectedComms] = useState<InAppCommunicationType[]>([])

  useEffect(() => {
    const stored = SafeLocalStorage.getItem(storageKey, null)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setDismissedComms(parsed)
      } catch {
        setDismissedComms([])
      }
    }
  }, [storageKey])

  const matchesPathPattern = (path: string, pattern: string): boolean => {
    if (pattern === 'all') return true

    // Check for exact match first and start with pattern
    if (path === pattern) return true
    if (path.startsWith(pattern + '/') || path === pattern) return true

    const regexPattern = pattern
      .replace(/\*/g, '[^/]+') // Replace * with regex for any non-slash characters
      .replace(/[.*?${}()|\\]/g, '\\$&') // Escape regex special characters, but not [ ] ^ +

    const regex = new RegExp(`^${regexPattern}$`)
    return regex.test(path)
  }

  // Update selected comms when data or pathname changes
  useUnsafeEffect(() => {
    if (!data || !canSeeInAppComms) {
      setSelectedComms([])
      return
    }

    // First, filter communications that match the current path and should be shown
    const eligibleComms =
      data.inAppComms?.communicationsConnection?.nodes?.filter((comm) => {
        const paths = comm?.onPath ?? []
        const matchesPath =
          paths.includes('all') || paths.some((path) => matchesPathPattern(pathname, path))
        if (!matchesPath) return false

        if (!shouldShowComm(comm.id)) return false

        return true
      }) ?? []

    // Sort by endDate (shortest endDate first) to prioritize communications that end sooner
    const sortedComms = eligibleComms.sort((a, b) => {
      const endDateA = new Date(a.schedule.endDate)
      const endDateB = new Date(b.schedule.endDate)

      // Sort by endDate, if same date just take the first one (natural array order)
      return endDateA < endDateB ? -1 : endDateA > endDateB ? 1 : 0
    })

    // Apply display rules based on communication types
    const firstBanner = sortedComms.find((comm) => comm.type === 'banner')
    const nonBanners = sortedComms.filter((comm) => comm.type !== 'banner')

    if (firstBanner) {
      // Show one banner + max one non-banner communication
      const result = [firstBanner]
      if (nonBanners.length > 0 && nonBanners[0]) {
        result.push(nonBanners[0])
      }
      setSelectedComms(result)
    } else {
      // Max 1 communication if none is a banner
      setSelectedComms(sortedComms.slice(0, 1))
    }
  }, [data, pathname, canSeeInAppComms])

  useUnsafeEffect(() => {
    const hasBanner = selectedComms.some((comm) => comm.type === 'banner')
    onHasBannerChange?.(hasBanner)
  }, [selectedComms, onHasBannerChange])

  const dismiss = (componentId: string) => {
    const comm = data?.inAppComms?.communicationsConnection?.nodes?.find(
      (c) => c.id === componentId
    )
    const showAgainAfterDays = comm?.onClose?.showAgainAfterSelectedDays ?? null

    const dismissedInfo: DismissedCommInfo = {
      id: componentId,
      dismissedAt: Date.now(),
      showAgainAfterDays,
      name: comm?.name ?? '',
    }

    const updatedDismissed = [
      ...(dismissedComms || []).filter((d) => d.id !== componentId), // Remove existing entry if any
      dismissedInfo,
    ]

    setDismissedComms(updatedDismissed)
    SafeLocalStorage.setItem(storageKey, JSON.stringify(updatedDismissed))

    // Remove the dismissed comm from selected comms
    setSelectedComms((prev) => prev.filter((communication) => communication.id !== componentId))
  }

  const shouldShowComm = (commId: string): boolean => {
    const dismissedInfo = dismissedComms?.find((d) => d.id === commId)
    if (!dismissedInfo) return true

    // If showAgainAfterDays is null, never show again
    if (dismissedInfo.showAgainAfterDays === null) return false

    // Check if enough days have passed
    const daysSinceDismissal = (Date.now() - dismissedInfo.dismissedAt) / (1000 * 60 * 60 * 24)
    return daysSinceDismissal >= dismissedInfo.showAgainAfterDays
  }

  return (
    <InAppCommsContext.Provider value={{ activeComms: selectedComms, dismiss }}>
      {children}
    </InAppCommsContext.Provider>
  )
}
